import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import {
  CATEGORY_STYLES,
  fmtDay,
  fmtRelease,
  fmtSurprise,
} from './indicatorFormat';

function EconomicIndicatorDetailModal({ isOpen, onClose, indicator }) {
  // Close on Escape — matches the pattern in PriceChartModal
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen || !indicator) return null;

  const category = indicator.category || 'Industry';
  const styles = CATEGORY_STYLES[category] || CATEGORY_STYLES.Industry;
  const format = indicator.format;
  const recent = indicator.recent_releases || [];
  const upcoming = indicator.upcoming_releases || [];
  const hasCalendar = recent.length > 0 || upcoming.length > 0;
  const sourceLabel = indicator.eodhd_type
    ? `Forecast & history: EODHD economic calendar`
    : 'No consensus forecast available for this series';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-gray-900 border border-gray-700 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between px-5 py-4 border-b border-gray-700 bg-gray-900">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${styles.bg} ${styles.text}`}>
                {category}
              </span>
              <h3 className="text-lg font-semibold text-white truncate">
                {indicator.display_title}
              </h3>
            </div>
            <p className="text-xs text-gray-500">
              FRED · {indicator.series_id}
              {indicator.frequency ? ` · ${indicator.frequency}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-800"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-6">
          {/* Upcoming releases */}
          <section>
            <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-2">
              Upcoming
            </h4>
            {upcoming.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                No upcoming releases on EODHD's calendar.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-800">
                      <th className="py-2 pr-4 text-left font-medium">Release Date</th>
                      <th className="py-2 pr-4 text-left font-medium">Period</th>
                      <th className="py-2 pr-4 text-right font-medium">Estimate</th>
                      <th className="py-2 pr-2 text-right font-medium">Previous</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcoming.map((r) => (
                      <tr key={`up-${r.date}-${r.period}`} className="border-b border-gray-800/60">
                        <td className="py-2 pr-4 text-gray-100">{fmtDay(r.date)}</td>
                        <td className="py-2 pr-4 text-gray-300">{r.period || '—'}</td>
                        <td className="py-2 pr-4 text-right text-gray-100">{fmtRelease(r.estimate, format)}</td>
                        <td className="py-2 pr-2 text-right text-gray-400">{fmtRelease(r.previous, format)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Recent releases */}
          <section>
            <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-2">
              Recent (last {recent.length || 0})
            </h4>
            {recent.length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                No historical releases available.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-800">
                      <th className="py-2 pr-4 text-left font-medium">Release Date</th>
                      <th className="py-2 pr-4 text-left font-medium">Period</th>
                      <th className="py-2 pr-4 text-right font-medium">Actual</th>
                      <th className="py-2 pr-4 text-right font-medium">Estimate</th>
                      <th className="py-2 pr-4 text-right font-medium">Previous</th>
                      <th className="py-2 pr-2 text-right font-medium">Surprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((r) => {
                      const surprise = r.actual != null && r.estimate != null
                        ? r.actual - r.estimate
                        : null;
                      const surpriseClass = surprise == null
                        ? 'text-gray-500'
                        : (surprise >= 0
                            ? (indicator.higher_is_bad ? 'text-red-400' : 'text-green-400')
                            : (indicator.higher_is_bad ? 'text-green-400' : 'text-red-400'));
                      return (
                        <tr key={`re-${r.date}-${r.period}`} className="border-b border-gray-800/60">
                          <td className="py-2 pr-4 text-gray-100">{fmtDay(r.date)}</td>
                          <td className="py-2 pr-4 text-gray-300">{r.period || '—'}</td>
                          <td className="py-2 pr-4 text-right text-white font-semibold">{fmtRelease(r.actual, format)}</td>
                          <td className="py-2 pr-4 text-right text-gray-300">{fmtRelease(r.estimate, format)}</td>
                          <td className="py-2 pr-4 text-right text-gray-400">{fmtRelease(r.previous, format)}</td>
                          <td className={`py-2 pr-2 text-right ${surpriseClass}`}>
                            {fmtSurprise(r.actual, r.estimate, format)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {!hasCalendar && (
            <p className="text-xs text-gray-500 italic">
              EODHD does not survey this series — no calendar data available.
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-800 text-[11px] text-gray-500">
          {sourceLabel}
        </div>
      </div>
    </div>
  );
}

export default EconomicIndicatorDetailModal;
